import React, { useState } from 'react';
import {
  X,
  MapPin,
  Calendar,
  Users,
  Target,
  DollarSign,
  Heart,
  Share2,
  MessageCircle,
  CheckCircle2,
  Clock,
  User
} from 'lucide-react';
import { CommunityProject } from '../../types/project.types';

interface ProjectDetailsProps {
  project: CommunityProject;
  onClose: () => void;
  isOpen: boolean;
}

const ProjectDetails: React.FC<ProjectDetailsProps> = ({ project, onClose, isOpen }) => {
  const [activeTab, setActiveTab] = useState('description');

  if (!isOpen) return null;

  const formatCurrency = (amount: number) => {
    return `${(amount / 1000000).toFixed(1)}M FCFA`;
  };

  const getStatusLabel = (status: string) => {
    const statusMap: Record<string, { label: string; color: string }> = {
      'idea': { label: 'Idée', color: 'bg-gray-100 text-gray-800' },
      'planning': { label: 'Planification', color: 'bg-blue-100 text-blue-800' },
      'fundraising': { label: 'Collecte de fonds', color: 'bg-blue-100 text-blue-800' },
      'approved': { label: 'Approuvé', color: 'bg-green-100 text-green-800' },
      'in_progress': { label: 'En cours', color: 'bg-blue-100 text-blue-800' },
      'completed': { label: 'Terminé', color: 'bg-green-100 text-green-800' },
      'on_hold': { label: 'En pause', color: 'bg-gray-100 text-gray-800' },
      'cancelled': { label: 'Annulé', color: 'bg-red-100 text-red-800' }
    };
    return statusMap[status] || statusMap['idea'];
  };

  const getPriorityLabel = (priority: string) => {
    const priorityMap: Record<string, { label: string; color: string }> = {
      'low': { label: 'Faible', color: 'bg-gray-100 text-gray-800' },
      'medium': { label: 'Moyenne', color: 'bg-blue-100 text-blue-800' },
      'high': { label: 'Élevée', color: 'bg-orange-100 text-orange-800' },
      'urgent': { label: 'Urgente', color: 'bg-red-100 text-red-800' }
    };
    return priorityMap[priority] || priorityMap['medium'];
  };

  const fundingPercentage = Math.round((project.budget_raised / project.funding_goal!) * 100);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-start justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden mt-8">
        {/* Header */}
        <div className="bg-blue-600 text-white px-6 py-4 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
          >
            <X size={24} />
          </button>

          <div className="pr-12">
            <div className="flex items-center gap-2 mb-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusLabel(project.status).color}`}>
                {getStatusLabel(project.status).label}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityLabel(project.priority_level).color}`}>
                Priorité: {getPriorityLabel(project.priority_level).label}
              </span>
              {project.featured && (
                <span className="bg-blue-200 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                  Projet vedette
                </span>
              )}
            </div>
            <h1 className="text-2xl font-bold mb-2">{project.title}</h1>
            <div className="flex items-center gap-4 text-blue-100">
              <div className="flex items-center gap-1">
                <MapPin size={16} />
                <span>{project.location_city}, {project.location_province}</span>
              </div>
              {project.start_date && (
                <div className="flex items-center gap-1">
                  <Calendar size={16} />
                  <span>Début: {new Date(project.start_date).toLocaleDateString('fr-FR')}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Navigation tabs */}
        <div className="border-b border-gray-200 px-6">
          <nav className="flex space-x-8">
            {[
              { id: 'description', label: 'Description', icon: MessageCircle },
              { id: 'funding', label: 'Financement', icon: DollarSign },
              { id: 'progress', label: 'Progression', icon: Target },
              { id: 'contact', label: 'Contact', icon: User }
            ].map(tab => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <IconComponent size={16} />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="px-6 py-6 max-h-96 overflow-y-auto">
          {activeTab === 'description' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Description du projet</h3>
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {project.description}
                </p>
              </div>

              {project.objectives && project.objectives.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Objectifs du projet</h3>
                  <ul className="space-y-2">
                    {project.objectives.map((objective, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle2 size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{objective}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Bénéficiaires</h3>
                <div className="flex items-center gap-2 text-gray-700">
                  <Users size={20} className="text-blue-600" />
                  <span>{project.target_beneficiaries}</span>
                </div>
              </div>

              {project.location_address && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Localisation précise</h3>
                  <div className="flex items-center gap-2 text-gray-700">
                    <MapPin size={20} className="text-red-600" />
                    <span>{project.location_address}</span>
                  </div>
                </div>
              )}

              {project.tags && project.tags.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Mots-clés</h3>
                  <div className="flex flex-wrap gap-2">
                    {project.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'funding' && (
            <div className="space-y-6">
              <div className="bg-gray-50 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">État du financement</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {formatCurrency(project.budget_raised)}
                    </div>
                    <div className="text-sm text-gray-600">Collecté</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {formatCurrency(project.funding_goal!)}
                    </div>
                    <div className="text-sm text-gray-600">Objectif</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">
                      {fundingPercentage}%
                    </div>
                    <div className="text-sm text-gray-600">Complété</div>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="mb-4">
                  <div className="w-full bg-gray-200 rounded-full h-4">
                    <div
                      className="bg-primary-green h-4 transition-all duration-300"
                      style={{ width: `${Math.min(fundingPercentage, 100)}%` }}
                    ></div>
                  </div>
                </div>

                <div className="text-sm text-gray-600 text-center">
                  Il reste {formatCurrency(project.funding_goal! - project.budget_raised)} à collecter
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-6">
                <h4 className="font-semibold text-gray-900 mb-3">Comment contribuer ?</h4>
                <div className="space-y-3 text-gray-700">
                  <div className="flex items-center gap-3">
                    <Heart size={20} className="text-red-500" />
                    <span>Participez au financement de ce projet communautaire</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Share2 size={20} className="text-blue-500" />
                    <span>Partagez le projet avec vos proches</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Users size={20} className="text-green-500" />
                    <span>Rejoignez l'équipe de bénévoles</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'progress' && (
            <div className="space-y-6">
              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Progression du projet</h3>
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span>Avancement général</span>
                    <span className="font-medium">{project.completion_percentage}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${project.completion_percentage}%` }}
                    ></div>
                  </div>
                </div>
              </div>

              {project.impact_metrics && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-4">Indicateurs d'impact</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(project.impact_metrics).map(([key, value]) => (
                      <div key={key} className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="text-sm text-gray-600 capitalize">
                          {key.replace(/_/g, ' ')}
                        </div>
                        <div className="text-xl font-semibold text-gray-900">
                          {typeof value === 'number' ? value.toLocaleString() : String(value)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-green-50 rounded-lg p-6">
                <h4 className="font-semibold text-gray-900 mb-3">Prochaines étapes</h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-gray-700">
                    <Clock size={16} className="text-orange-500" />
                    <span>Finalisation de la collecte de fonds</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-700">
                    <Target size={16} className="text-blue-500" />
                    <span>Lancement des travaux</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-700">
                    <CheckCircle2 size={16} className="text-green-500" />
                    <span>Mise en service du projet</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations de contact</h3>
                <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                  {project.project_leader_id && (
                    <div className="flex items-center gap-3">
                      <User size={20} className="text-blue-600" />
                      <div>
                        <div className="font-medium text-gray-900">Chef de projet</div>
                        <div className="text-gray-600">ID: {project.project_leader_id}</div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    <MapPin size={20} className="text-red-600" />
                    <div>
                      <div className="font-medium text-gray-900">Localisation</div>
                      <div className="text-gray-600">
                        {project.location_city}, {project.location_department}, {project.location_province}
                      </div>
                      {project.location_address && (
                        <div className="text-sm text-gray-500">{project.location_address}</div>
                      )}
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <div className="text-sm text-gray-600">
                      Projet créé le {new Date(project.created_at).toLocaleDateString('fr-FR')}
                      {project.updated_at !== project.created_at && (
                        <span> • Dernière mise à jour le {new Date(project.updated_at).toLocaleDateString('fr-FR')}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h4 className="font-semibold text-gray-900 mb-3">Vous voulez vous impliquer ?</h4>
                <p className="text-gray-700 mb-4">
                  Contactez l'équipe du projet pour participer en tant que bénévole,
                  partenaire ou contributeur financier.
                </p>
                <div className="space-y-2">
                  <button className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors">
                    Rejoindre le projet
                  </button>
                  <button className="w-full border border-blue-600 text-blue-600 py-2 px-4 rounded-lg hover:bg-blue-50 transition-colors">
                    Contacter l'équipe
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Dernière mise à jour: {new Date(project.updated_at).toLocaleDateString('fr-FR')}
            </div>
            <div className="flex gap-2">
              <button className="p-2 text-gray-600 hover:text-blue-600 transition-colors">
                <Share2 size={20} />
              </button>
              <button className="p-2 text-gray-600 hover:text-red-600 transition-colors">
                <Heart size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetails;